import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { MessagesSquare, Server, Wrench } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ROUTES } from "@/app/routes";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { OperatorConsoleFooter } from "./operator-console-footer";
import { OperatorConsoleStatusStrip } from "./operator-console-status-strip";
import { OperatorConsoleToolbar } from "./operator-console-toolbar";
import { useOperatorConsoleAccess } from "./use-operator-console-access";

const shellPanels = [
  {
    title: "Session Navigator",
    description: "Reserved for searchable agent and session switching, derived titles, and route-driven session focus.",
  },
  {
    title: "Maintenance Thread",
    description: "Reserved for streamed assistant output, tool events, thinking visibility, and operator status banners.",
  },
  {
    title: "Runtime Panels",
    description: "Reserved for reusing the existing assets and task panels without dragging operator controls into user chat.",
  },
];

export function OperatorConsolePage() {
  const navigate = useNavigate();
  const { tenantId } = useTenantContext();
  const { sessionId } = useParams<{ sessionId?: string }>();
  const { hasAccess, isLoading, isPlatformAdmin, isTenantAdmin } = useOperatorConsoleAccess();

  useDocumentTitle("Operator Console");

  const accessLabel = useMemo(() => {
    if (isPlatformAdmin) {
      return "Platform Admin";
    }
    if (isTenantAdmin) {
      return "Tenant Admin";
    }
    return "Access Pending";
  }, [isPlatformAdmin, isTenantAdmin]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading operator console</CardTitle>
          <CardDescription>
            Resolving operator access and preparing the dedicated maintenance surface.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!hasAccess) {
    return (
      <EmptyState
        icon={<Wrench size={40} strokeWidth={1.2} />}
        title="Operator console access required"
        description="This maintenance surface is reserved for platform admins and tenant admins."
        action={{
          label: "Open conversations",
          onClick: () => navigate(ROUTES.tenantConversations(tenantId)),
        }}
      />
    );
  }

  return (
    <div className="grid gap-4">
      <OperatorConsoleToolbar
        accessLabel={accessLabel}
        selectedSessionId={sessionId}
        canInspectUsers={isPlatformAdmin}
        onInspectUsers={() => navigate(ROUTES.tenantAdminConversationsUsers(tenantId))}
        onClearSessionRoute={() => navigate(ROUTES.tenantOperatorConsole(tenantId))}
      />

      <OperatorConsoleStatusStrip selectedSessionId={sessionId} />

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)_320px]">
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>{shellPanels[0].title}</CardTitle>
            <CardDescription>{shellPanels[0].description}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 pt-4 text-sm" style={{ color: "var(--muted-foreground)" }}>
            <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
              Dedicated searchable session and agent navigation lands here.
            </div>
            <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
              Route helpers are already wired for `/workspace/operator-console` and `/workspace/operator-console/:sessionId`.
            </div>
            {sessionId ? (
              <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
                This scaffold is currently pinned to route session <span style={{ color: "var(--text-strong)" }}>{sessionId}</span>.
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle>{shellPanels[1].title}</CardTitle>
                <CardDescription>{shellPanels[1].description}</CardDescription>
              </div>
              <Badge variant="outline">Thread shell reserved</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <EmptyState
              icon={<MessagesSquare size={40} strokeWidth={1.2} />}
              title="Maintenance thread scaffolded"
              description="This center panel is where the shared conversation thread primitives will be reused for streamed operator output, tool events, and TUI-style status handling."
            />
          </CardContent>
        </Card>

        <div className="grid gap-4">
          <Card>
            <CardHeader className="border-b border-border">
              <CardTitle>{shellPanels[2].title}</CardTitle>
              <CardDescription>{shellPanels[2].description}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 pt-4 text-sm" style={{ color: "var(--muted-foreground)" }}>
              <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
                Existing assets and task panels stay reusable primitives.
              </div>
              <div className="rounded-md border p-3" style={{ borderColor: "var(--border)" }}>
                The operator console will compose them around richer runtime controls instead of replacing them.
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-border">
              <div className="flex items-center gap-2">
                <Server className="size-4" />
                <CardTitle>Backend Gap Placeholder</CardTitle>
              </div>
              <CardDescription>
                Session patch, reset, gateway status, model inventory, and slash-command registry endpoints still need to land.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.tenantRuntime(tenantId))}>
                Open Runtime Surface
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <OperatorConsoleFooter />
    </div>
  );
}
